import { IkeUnit } from '@ikegps/core-shared';
import { debounce } from 'lodash';
import { action, override, makeObservable } from 'mobx';
import { InputState } from './input.state';
import { NumberInputState } from './number.input.state';

export class DistanceInputState extends InputState<number> {
	meters: NumberInputState;
	feet: NumberInputState;
	inch: NumberInputState;

	static readonly METER_PRECISION: number = 3;
	static readonly INCH_PRECISION: number = 1;

	static equal(value1: number, value2: number): boolean {
		const inches1 = DistanceInputState.metersAsInches(value1);
		const inches2 = DistanceInputState.metersAsInches(value2);

		return NumberInputState.equal(inches1, inches2, DistanceInputState.INCH_PRECISION);
	}

	private static metersAsInches(value: number): number {
		return IkeUnit.round(IkeUnit.convert(value, IkeUnit.INCH, IkeUnit.METER), NumberInputState.DEFAULT_PRECISION);
	}

	constructor(label: string) {
		super(label);

		makeObservable(this);

		this.isLabelShown = false;

		const debounceMetric = debounce(() => this.updateMetric(), 600);
		const debounceImperial = debounce(() => this.updateImperial(), 600);

		const metricOptions = {
			onblur: () => debounceMetric.flush(),
			oninput: () => debounceMetric(),
		};
		const imperialOptions = {
			onblur: () => debounceImperial.flush(),
			oninput: () => debounceImperial(),
		};

		this.meters = new NumberInputState('m', DistanceInputState.METER_PRECISION, undefined, metricOptions);
		this.feet = new NumberInputState('ft', undefined, undefined, imperialOptions);
		this.inch = new NumberInputState('in', DistanceInputState.INCH_PRECISION, undefined, imperialOptions);

		this.meters.isLabelShown = false;
		this.feet.isLabelShown = false;
		this.inch.isLabelShown = false;

		this.meters.hideZero = true;
		this.feet.hideZero = true;
		this.inch.hideZero = true;
	}

	@override override setValue(meters: number | null) {
		this._value = meters;

		if (this.value == null || isNaN(this.value)) {
			this.meters.setValue(null);
			this.feet.setValue(null);
			this.inch.setValue(null);
			return;
		}

		const inches = DistanceInputState.metersAsInches(this.value);
		const feetInch = IkeUnit.splitFeetInch(inches);

		if (feetInch.feet !== this.feet.value) {
			this.feet.setValue(feetInch.feet !== 0 ? feetInch.feet : null);
		}
		if (feetInch.inches !== this.inch.value) {
			this.inch.setValue(feetInch.inches !== 0 ? feetInch.inches : null);
		}

		this.meters.setValue(this.value !== 0 ? this.value : null);
	}

	override hasError() {
		return this.meters.hasError() || this.feet.hasError() || this.inch.hasError();
	}

	@action.bound updateImperial() {
		if (this.hasError()) {
			return;
		}

		if (this.feet.value == null && this.inch.value == null) {
			this.setValue(null);
		} else {
			const inches = IkeUnit.joinFeetInch(this.feet.value || 0, this.inch.value || 0);
			const meters = IkeUnit.convert(inches, IkeUnit.METER, IkeUnit.INCH);
			this.setValue(meters);
		}
	}

	@action.bound updateMetric() {
		if (this.hasError()) {
			return;
		}

		this.setValue(this.meters.value);
	}

	override set isAutoFocused(isFocus: boolean) {
		if (this.feet == null) {
			return;
		}
		this.meters.isAutoFocused = isFocus;
		this.feet.isAutoFocused = isFocus;
	}

	override get isAutoFocused(): boolean {
		return this.meters.isAutoFocused;
	}
}
